import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/select';
import { LoadingPage } from '@/components/ui/loading';
import { EmptyState } from '@/components/ui/empty-state';
import { StatStrip } from '@/components/ui/stat-strip';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { formatRelativeTime, formatDateTime } from '@/lib/utils';
import { AlertStatus, AlertType, UserRole } from '@/types/api';
import type { Property } from '@/types/api';
import { getProperties } from '@/features/properties/api/properties-api';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { useAlerts } from '../hooks/use-alerts';

const statusColors: Record<AlertStatus, 'error' | 'warning' | 'success'> = {
  [AlertStatus.Active]: 'error',
  [AlertStatus.Acknowledged]: 'warning',
  [AlertStatus.Resolved]: 'success',
};

// Alert Type badge tone — uniformly red across all types so the Alerts
// page matches the Property page (PropertyDeviceCard renders the alert
// badge as variant="error" regardless of type) and the Device Details
// "Active alarms" tile (always error tone). Yellow stays reserved for
// the Acknowledged status. Severity nuance used to live in this map but
// it competed with the status-color convention QA asked us to honor.
const typeColors: Record<AlertType, 'error'> = {
  PipeEmpty: 'error',
  NoUsage30Days: 'error',
  MinTemperatureExceeded: 'error',
  MaxTemperatureExceeded: 'error',
  LowSignal: 'error',
  BurstPipeDetected: 'error',
  LeakDetected: 'error',
  ReverseFlowDetected: 'error',
  LowBattery: 'error',
  LowTemperature: 'error',
  SensusReadError: 'error',
  CoverRemoved: 'error',
};

const typeLabels: Record<AlertType, string> = {
  PipeEmpty: 'Pipe Empty',
  NoUsage30Days: 'No Usage (30d)',
  MinTemperatureExceeded: 'Min Temp Exceeded',
  MaxTemperatureExceeded: 'Max Temp Exceeded',
  LowSignal: 'Low Signal',
  BurstPipeDetected: 'Burst Pipe',
  LeakDetected: 'Leak Detected',
  ReverseFlowDetected: 'Reverse Flow',
  LowBattery: 'Low Battery',
  LowTemperature: 'Low Temperature (SALT)',
  SensusReadError: 'Sensus Read Error',
  CoverRemoved: 'Cover Removed',
};

export function AlertsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const propertyIdParam = searchParams.get('propertyId') ?? '';
  const deviceIdParam = searchParams.get('deviceId') ?? '';
  const {
    alerts,
    loading,
    filterStatus,
    setFilterStatus,
    activeCount,
    acknowledgedCount,
    resolvedCount,
    handleAcknowledge,
    handleResolve,
  } = useAlerts({
    propertyId: propertyIdParam || undefined,
    deviceId: deviceIdParam || undefined,
  });

  // Gate the Acknowledge/Resolve actions to roles that can manage alerts.
  // Mirrors the backend `[ActiveRole("Owner,Admin,Manager")]` on
  // AlertsController.AcknowledgeAlert / ResolveAlert. Owner-anywhere
  // short-circuit in `useActiveRole` handles sysadmins + Owner-in-any-org.
  const activeRole = useActiveRole();
  const canManageAlerts =
    activeRole === UserRole.Owner ||
    activeRole === UserRole.Admin ||
    activeRole === UserRole.Manager;

  function clearDeviceFilter() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('deviceId');
        return next;
      },
      { replace: true }
    );
  }
  // Device-filter chip caption — surfaces the serial of the filtered
  // device so the user can tell where the link came from.
  const filteredDeviceSerial = deviceIdParam
    ? alerts.find((a) => a.deviceId === deviceIdParam)?.serialNumber ?? deviceIdParam
    : null;

  // Property dropdown options. Fetched once per session — small list, low
  // churn. Selection writes back to ?propertyId= so the URL is shareable
  // and refreshable, and so links from property pages (Item 2) land
  // pre-filtered without extra plumbing.
  const [properties, setProperties] = useState<Property[]>([]);
  useEffect(() => {
    getProperties({ perPage: 100 })
      .then((res) => setProperties(res.items))
      .catch(() => setProperties([]));
  }, []);

  function handlePropertyChange(value: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('propertyId', value);
        else next.delete('propertyId');
        return next;
      },
      { replace: true }
    );
  }

  if (loading) return <LoadingPage />;

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Stats — slim three-cell strip matching the property page so
              QA's "less scrolling to the pertinent information" feedback
              applies on the alerts surface too. Tones: Active=error/red,
              Acknowledged=warning/yellow, Resolved=success/green. Each
              cell clicks through to the corresponding status filter. */}
          <StatStrip
            cells={[
              {
                icon: <AlertTriangle className="w-4 h-4" />,
                label: 'Active Alerts',
                shortLabel: 'Active',
                value: activeCount,
                tone: 'error',
                onClick: () => setFilterStatus(AlertStatus.Active),
              },
              {
                icon: <Clock className="w-4 h-4" />,
                label: 'Acknowledged',
                shortLabel: 'Acknowledged',
                value: acknowledgedCount,
                tone: 'warning',
                onClick: () => setFilterStatus(AlertStatus.Acknowledged),
              },
              {
                icon: <CheckCircle className="w-4 h-4" />,
                label: 'Resolved (7d)',
                shortLabel: 'Resolved',
                value: resolvedCount,
                tone: 'success',
                onClick: () => setFilterStatus(AlertStatus.Resolved),
              },
            ]}
          />

          {/* Filters */}
          <Card>
            <div className="p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="flex gap-2">
                <Button variant={filterStatus === AlertStatus.Active ? 'primary' : 'ghost'} size="sm" onClick={() => setFilterStatus(AlertStatus.Active)}>Active</Button>
                <Button variant={filterStatus === AlertStatus.Acknowledged ? 'primary' : 'ghost'} size="sm" onClick={() => setFilterStatus(AlertStatus.Acknowledged)}>Acknowledged</Button>
                <Button variant={filterStatus === AlertStatus.Resolved ? 'primary' : 'ghost'} size="sm" onClick={() => setFilterStatus(AlertStatus.Resolved)}>Resolved</Button>
              </div>
              <div className="w-full sm:w-64">
                <Select
                  value={propertyIdParam}
                  onChange={handlePropertyChange}
                  placeholder="All properties"
                  searchable
                  options={[
                    { value: '', label: 'All properties' },
                    ...properties.map((p) => ({ value: p.id, label: p.name })),
                  ]}
                />
              </div>
            </div>
            {filteredDeviceSerial && (
              <div className="px-4 pb-4 -mt-1 flex items-center gap-2">
                <Badge variant="info" size="sm">
                  Device: {filteredDeviceSerial}
                </Badge>
                <button
                  type="button"
                  onClick={clearDeviceFilter}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Clear device filter
                </button>
              </div>
            )}
          </Card>

          {/* Alerts Table */}
          {alerts.length === 0 ? (
            <Card variant="elevated">
              <EmptyState
                icon={<CheckCircle className="w-16 h-16" />}
                title={filterStatus ? `No ${filterStatus.toLowerCase()} alerts` : 'No alerts'}
                description={filterStatus ? 'Try changing the filter to see more alerts.' : 'All systems are operating normally.'}
              />
            </Card>
          ) : (
            <Card variant="elevated" padding="none">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Transmission</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.map((alert) => (
                    <TableRow key={alert.id}>
                      <TableCell>
                        <Badge variant={typeColors[alert.alertType]} size="sm">
                          {typeLabels[alert.alertType]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {alert.deviceId ? (
                          <button className="text-left hover:underline" onClick={() => navigate(`/devices/${alert.deviceId}`)}>
                            <p className="text-sm font-medium text-primary-600 dark:text-primary-400">{alert.serialNumber}</p>
                            <code className="font-mono text-xs text-gray-500 dark:text-gray-400">{alert.devEui}</code>
                          </button>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{alert.serialNumber}</p>
                            <code className="font-mono text-xs text-gray-500 dark:text-gray-400">{alert.devEui}</code>
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusColors[alert.status]} size="sm" dot>{alert.status}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {formatDateTime(alert.lastTransmissionAt)}
                      </TableCell>
                      <TableCell className="text-gray-600 dark:text-gray-400">
                        {formatRelativeTime(alert.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-2">
                          {canManageAlerts && (
                            <>
                              {alert.status === 'Active' && (
                                <Button variant="outline" size="sm" onClick={() => handleAcknowledge(alert)}>
                                  Acknowledge
                                </Button>
                              )}
                              {alert.status !== 'Resolved' && (
                                <Button variant="primary" size="sm" onClick={() => handleResolve(alert)}>
                                  Resolve
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
